import { Button, Modal, Stack, Text, Textarea } from "@assetwatch/ui";
import { useEffect, useState } from "react";
import {
  type BulkActionOutcome,
  useBulkCompleteWorkOrdersMutation,
} from "../../queries/useWorkOrderBulkActionsMutations";
import type { WorkOrder } from "../../queries/useWorkOrdersQuery";
import { displayWorkOrderNumber } from "../snowflakes/work-orders/presentation";
import {
  completionIneligibilityReason,
  isEligibleForCompletion,
} from "./bulkEligibility";

type Step = "review" | "results";

export function BulkMarkAsDoneModal({
  open,
  onOpenChange,
  workOrders,
  onOutcome,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrders: WorkOrder[];
  onOutcome: (outcome: BulkActionOutcome) => void;
}) {
  const mutation = useBulkCompleteWorkOrdersMutation();
  const [step, setStep] = useState<Step>("review");
  const [completionNotes, setCompletionNotes] = useState("");
  const [outcome, setOutcome] = useState<BulkActionOutcome | null>(null);

  const eligible = workOrders.filter(isEligibleForCompletion);
  const ineligible = workOrders.filter(
    (workOrder) => !isEligibleForCompletion(workOrder),
  );

  useEffect(() => {
    if (open) {
      setStep("review");
      setCompletionNotes("");
      setOutcome(null);
      mutation.reset();
    }
  }, [open, mutation.reset]);

  async function confirm() {
    try {
      const result = await mutation.mutateAsync({
        targets: eligible.map((workOrder) => ({
          id: workOrder.id,
          number: workOrder.number,
        })),
        completionNotes: completionNotes.trim(),
      });
      setOutcome(result);
      onOutcome(result);
      setStep("results");
    } catch {
      // mutation.isError drives the inline error message; stay on the review step.
    }
  }

  return (
    <Modal
      onOpenChange={onOpenChange}
      open={open}
      size="md"
      title={
        step === "review" ? "Mark Work Orders as done" : "Mark as done results"
      }
    >
      {step === "review" ? (
        <Stack gap={16}>
          <Text>
            {eligible.length} of {workOrders.length} selected Work Orders can be
            marked as done.
          </Text>
          {ineligible.length > 0 ? (
            <Stack gap={8}>
              <Text size="sm" tone="subtle">
                Not eligible
              </Text>
              {ineligible.map((workOrder) => (
                <div
                  className="flex items-center justify-between gap-(--spacing-8) rounded-(--radius-sm) border border-outline-subtle px-(--spacing-12) py-(--spacing-8)"
                  key={workOrder.id}
                >
                  <Text size="sm">
                    {displayWorkOrderNumber(workOrder.number)} —{" "}
                    {workOrder.title}
                  </Text>
                  <Text size="sm" tone="subtle">
                    {completionIneligibilityReason(workOrder)}
                  </Text>
                </div>
              ))}
            </Stack>
          ) : null}
          {eligible.length > 0 ? (
            <Textarea
              aria-label="Completion notes"
              label="Completion notes"
              onChange={(event) => setCompletionNotes(event.target.value)}
              value={completionNotes}
            />
          ) : null}
          {mutation.isError ? (
            <Text tone="negative">
              Unable to complete the selected Work Orders. Try again.
            </Text>
          ) : null}
          <div className="flex justify-end gap-(--spacing-8)">
            <Button onClick={() => onOpenChange(false)} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={
                eligible.length === 0 ||
                !completionNotes.trim() ||
                mutation.isPending
              }
              loading={mutation.isPending}
              loadingText="Marking as done"
              onClick={() => void confirm()}
            >
              Mark as done
            </Button>
          </div>
        </Stack>
      ) : (
        <Stack gap={16}>
          <Text>
            {outcome?.succeeded.length ?? 0} of{" "}
            {(outcome?.succeeded.length ?? 0) + (outcome?.failed.length ?? 0)}{" "}
            Work Orders were marked as done.
          </Text>
          {outcome && outcome.failed.length > 0 ? (
            <Stack gap={8}>
              <Text size="sm" tone="negative">
                Could not be marked as done
              </Text>
              {outcome.failed.map((item) => (
                <div
                  className="flex items-center justify-between gap-(--spacing-8) rounded-(--radius-sm) border border-outline-subtle px-(--spacing-12) py-(--spacing-8)"
                  key={item.id}
                >
                  <Text size="sm">{displayWorkOrderNumber(item.number)}</Text>
                  <Text size="sm" tone="negative">
                    {item.reason}
                  </Text>
                </div>
              ))}
            </Stack>
          ) : null}
          <div className="flex justify-end gap-(--spacing-8)">
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </Stack>
      )}
    </Modal>
  );
}
